import lodash from 'lodash'
import CurrencyFormatter from 'currency-formatter'
import store from '@/store'
import dateUtil from '@/utils/dateUtil'
import i18n from '@/utils/i18n'
import userHelper from '@/utils/userHelper'
import { addendumChangeType, vendorBidStatus, queryStatus, queryStatusMap } from '@/utils/locale'
import { contactTypes } from '@/api/users/consts'
import { dateFormat } from '@/utils/regex'
import { formatConditionsOfContracts } from '@/modules/tenders/utils'
import { getLink, getVendorHomeLink } from '@/api/postman/util'
import { getTermsConditions, getAttachments } from '@/api/docs/request'
import { getTotalAmount } from '@/utils/tenderUtils'
import { getVendorDetailByIdAndSite } from '@/api/users/request'
import { getIssueDateArr } from '@/pages/tenders/bidMixin'
import { filterAddendumRemovedItems } from './tenderBid.serviceItems'

const notApplicable = 'N/A'

export function getTenderPicContacts (tender) {
  const managerContacts = lodash.get(tender, 'manager.contacts', [])
  const picPhone = managerContacts.find(item => item.priority && item.type === contactTypes.PHONE_NUMBER)
  const picEmail = managerContacts.find(item => item.priority && item.type === contactTypes.EMAIL)

  return {
    picPhone: picPhone ? picPhone.value : i18n.t('emptyValue'),
    picEmail: picEmail ? picEmail.value : i18n.t('emptyValue'),
  }
}

export async function buildRequest (originData = { general: {} }, bidServices = {}) {
  const general = { ...originData.general }
  let contractorName
  general.issueHistories = originData.issueDateArr || []
  if (originData.bid) {
    if (originData.bid.visitDate) {
      general.visitDate = dateUtil.format(originData.bid.visitDate, 'DD MMM YY, HH:mm')
    }

    const isContractor = store && store.getters['user/isContractor']
    const currentUserContact = (store && store.getters['user/currentUserContact']) || {}
    if (!isContractor && originData.bid.status === vendorBidStatus.SUBMITTED && originData.bid.respondedOn) {
      general.respondedOn = dateUtil.format(originData.bid.respondedOn, 'DD MMM YY, HH:mm')
    }

    contractorName = deep(originData, 'tncInfo.companyName') || currentUserContact.name
  }
  const picContacts = getTenderPicContacts(general)
  Object.assign(general, picContacts)

  const cocI18nPath = store && store.getters['tenders/cocI18nPath']
  const isOldTerm = store && store.getters['tenders/isOldTerm']
  if (general.conditionsOfContracts && i18n.te(cocI18nPath)) {
    let cocType = general.conditionsOfContracts
    general.conditionsOfContracts = formatConditionsOfContracts(cocType, isOldTerm, cocI18nPath)
  }
  const engineerFiles = deep(general, 'documents') || []
  const request = {
    id: general.id,
    currencyCode: lodash.get(general, 'purchaseRequisition.currencyCode'),
    isTender: general.tenderType === 'TENDER',
    general,
    sow: { ...general.scopeOfWorks },
    itemServices: [...originData.itemServices],
    engineerFiles,
    vendorAttachments: { ...originData.attachments },
    tnc: { ...originData.tncInfo },
    link: window.location.href,
    contractorName,
  }
  return getPDFData(request, bidServices, originData.bid.status)
}

export async function buildQueryRequest (originData = { general: {} }) {
  let request = {}
  const bid = originData.bid || {}
  request.prNumber = originData.general.prNumber
  request.prDescription = originData.general.prDescription
  const queries = bid.queries || []

  const isContractor = store && store.getters['user/isContractor']
  const user = store.getters['user/currentUser'] || {}

  await Promise.all(
    queries.map(async query => {
      query.closingDate = formatDate(query.closingDate)
      if (isContractor) {
        query.issuedDate = null
      } else {
        query.issuedDate = formatDate(query.issuedDate)
      }
      query.submittedDate = formatDate(query.submittedDate)
      query.vendorFiles = await getAttachments(query.id)
      query.engineerFiles = await getAttachments(query.intResourceId)
      if (query.status === queryStatus.ISSUED && !isContractor) {
        (query.questions || []).forEach(q => q.answer = null) // eslint-disable-line
      } else if (queryStatus.NO_RESPONSE === query.status) {
        (query.questions || []).forEach(q => q.answer = queryStatusMap.NO_RESPONSE) // eslint-disable-line
      }
      if (queryStatus.SUBMITTED !== query.status && !isContractor) {
        query.vendorFiles = null
      }
      return query
    }),
  )
  request.queries = lodash.orderBy(queries, 'order').reverse()
  const [vendorDetail] = await getVendorDetailByIdAndSite(bid.vendorId, originData.general.site)
  request.vendorDetail = vendorDetail
  request.contractorName = deep(originData, 'tncInfo.companyName') || deep(request, 'company.name')
  request.link = isContractor ? getVendorHomeLink() : getLink('queryStatus', originData.general.id)
  request.username = isContractor ? getContractorName(user) : userHelper.getUserName(user.username, user.displayName)
  return request
}

function getContractorName (user = {}) {
  const userContact = store.getters['user/currentUserContact'] || {}
  return userContact.contactAdmin || userHelper.getUserName(user.username, user.displayName)
}

async function getPDFData (request = {}, bidServices = {}, bidStatus) {
  const validityPeriod = deep(request, 'general.validityPeriod')
  updateGeneral(request.general)
  request.itemServices = updateItemServices(request.itemServices, bidServices, deep(request, 'general.allowServiceAddOthers'))
  setAttachments(request)
  const totalAmount = (request.itemServices || {}).totalAmount
  const terms = await buildTerms({ general: request.general, isTender: request.isTender, validityPeriod, totalAmount, bidStatus })
  request.tnc = { ...request.tnc, ...terms }

  const isContractor = store && store.getters['user/isContractor']
  request.showRemarks = isContractor ? ([vendorBidStatus.INVITED, vendorBidStatus.DRAFT].includes(bidStatus) || bidServices.remark) : bidServices.remark
  return request
}

function updateGeneral (general = {}) {
  general.workingHours = setWorkingHours(general.workingHours)
  general.noisyWorkingHours = setWorkingHours(general.noisyWorkingHours)
  general.closingDate = formatDate(general.closingDate)
  general.issuedDate = dateUtil.formatDate(general.issuedDate, 'MMM dd')
  if (general.version > 0) {
    general.lastIssuedDate = general.issuedDate
  }
  const isOldTerm = store.getters['tenders/isOldTerm']
  const validityPeriodLabel = i18n.t('tender.general.validityPeriodLabel')
  general.validityPeriod = `${general.validityPeriod} ${validityPeriodLabel}`
  const projectStart = general.projectStart
  general.projectStart = isOldTerm ? i18n.t(`tender.general.oldProjectStartDesc`, {projectStart})
    : i18n.t(`tender.general.projectStartDesc`, {projectStart})
  if (general.onsiteVisitRequired) {
    general.onsiteContact = `${deep(general, 'onsiteContact.name')}\n${deep(general, 'onsiteContact.email')}\n${deep(general, 'onsiteContact.phone')}`
  } else {
    general.onsiteContact = ''
  }
  general.onsiteVisitRequired = i18n.t(general.onsiteVisitRequired ? 'yes' : 'no')
  general.showAdditionalItems = shouldShowAdditionalItems(general.optionalItems)
  delete general.firstIssuedVersion
}

function shouldShowAdditionalItems (optionalItems = []) {
  if (!optionalItems) return false
  const checkedOptions = optionalItems
    .filter(item => item.selected)
  return checkedOptions.length > 0
}

// TODO @Michelle <Michelle_Zhang@epam.com> workingHours is not mandatory now, need to check PDF
function setWorkingHours (workingHours = []) {
  return workingHours.map(workingHour => {
    let value = workingHour.workingDayFrom
    if (workingHour.workingDayTo && workingHour.workingDayTo !== '--/--' && workingHour.workingDayTo !== workingHour.workingDayFrom) {
      value = `${value} - ${workingHour.workingDayTo}`
    }
    value = `${value}\t\t${workingHour.workingHourFrom}`
    if (workingHour.workingHourTo && workingHour.workingHourTo !== '--/--' && workingHour.workingHourTo !== workingHour.workingHourFrom) {
      value = `${value} - ${workingHour.workingHourTo}`
    }
    return value
  })
}

function formatDate (date) {
  if (!date || isNaN(dateUtil.toMoment(date))) return ''
  const dateTime = dateUtil.convertDateToArray(dateUtil.toMoment(date), dateFormat, '-')
  return `${dateTime[0]} ${dateTime[1]}`
}

function updateItemServices (services = [], bidServices = {}, allowOtherItems = false) {
  let items = []
  let bid = {}
  const isContractor = store && store.getters['user/isContractor']
  const itemQtys = bidServices.bidItemQtys || {}
  const itemRates = bidServices.bidItemRates || {}
  let totalBeforeDiscount = 0
  services.forEach(section => {
    if (section.addendumLabel === addendumChangeType.removed) {
      return
    }
    let sectionTotal = 0
    let itemSection = { ...section }
    let lineItems = [];
    (section.lineItems || []).forEach(item => {
      if (item.addendumLabel === addendumChangeType.removed) {
        return
      }
      if (!section.forReference) {
        totalBeforeDiscount += Number(item.total) || 0
      }
      sectionTotal += Number(item.total) || 0
      item.itemTotal = CurrencyFormatter.format(item.total, 'USD')
      if (itemQtys[item.id]) {
        item.itemQty = itemQtys[item.id]
      } else if (item.id in itemQtys && !isContractor) {
        item.itemQty = notApplicable
      }
      if (itemRates[item.id]) {
        item.itemRate = CurrencyFormatter.format(itemRates[item.id], 'USD')
      } else if (!isContractor) {
        item.itemRate = notApplicable
      }

      if (!isContractor && [item.itemQty, item.itemRate].includes(notApplicable)) {
        item.itemTotal = notApplicable
      }
      let options = [];
      (item.options || []).forEach(option => {
        if (option.addendumLabel === addendumChangeType.removed) {
          return
        }
        if (itemRates[option.id]) {
          option.optionRate = CurrencyFormatter.format(itemRates[option.id], 'USD')
        }
        options.push(option)
      })
      item.options = options
      lineItems.push(item)
    })
    itemSection.lineItems = lineItems
    const isAllNA = itemSection.lineItems.every(item => item.itemTotal === notApplicable)
    itemSection.total = (isAllNA && !isContractor) ? notApplicable : CurrencyFormatter.format(sectionTotal, 'USD')
    if (!itemSection.forReference || itemSection.lineItems.length > 0) {
      items.push(itemSection)
    }
  })
  bid.remark = bidServices.remark
  let others = {}

  if (bidServices.otherItem && bidServices.otherItem.desc) {
    let oldOther = {}
    const formatedRate = CurrencyFormatter.format(bidServices.otherItem.rate, 'USD')
    const itemRate = isContractor && bidServices.otherItem.rate.trim().length === 0 ? '' : formatedRate

    oldOther.lineItems = [{
      itemNo: 1,
      itemQty: 1,
      itemUnit: 'Lot',
      itemRate,
      itemTotal: formatedRate,
      itemDesc: bidServices.otherItem.desc,
    }]
    oldOther.title = i18n.t('tender.section.others')
    totalBeforeDiscount += Number(bidServices.otherItem.rate)
    oldOther.total = formatedRate
    items.push(oldOther)
  }

  let otherItems = []
  if (allowOtherItems && bidServices.otherItems && bidServices.otherItems.length) {
    otherItems = lodash.cloneDeep(bidServices.otherItems)
    otherItems.forEach((item, index) => {
      item.itemNo = index + 1
      item.itemTotal = CurrencyFormatter.format(item.total, 'USD')
      item.itemRate = isContractor && item.rate.trim().length === 0 ? '' : CurrencyFormatter.format(item.rate, 'USD')
    })

    others.title = i18n.t('tender.section.others')
    others.lineItems = otherItems
    totalBeforeDiscount += Number(bidServices.totalOfOthers)
    others.total = CurrencyFormatter.format(bidServices.totalOfOthers, 'USD')
    items.push(others)
  }
  bid.items = items
  const discountValue = bidServices.discountValue
  bid.discount = `${discountValue} ${bidServices.discountType}`
  bid.totalBeforeDiscount = CurrencyFormatter.format(totalBeforeDiscount, 'USD')
  const totalFee = getTotalAmount(bidServices, totalBeforeDiscount)
  bid.totalAmount = CurrencyFormatter.format(totalFee, 'USD')
  return bid
}

function setAttachments (request = {}) {
  if (request.engineerFiles && request.engineerFiles.length > 0) {
    request.showEngineerAttachment = true
  }
  if (request.vendorAttachments && (request.vendorAttachments.files || []).length > 0) {
    request.showVendorAttachment = true
  }
  request.showAttachment = request.showEngineerAttachment || request.showVendorAttachment
}

async function buildTerms ({ general, isTender = false, validityPeriod = 180, totalAmount = 0, tncCheckedList, bidStatus }) {
  const tnc = {}
  const isContractor = store && store.getters['user/isContractor']
  if (!tncCheckedList || tncCheckedList.length === 0) {
    const storeTncCheckedList = store.getters['tenders/tncChecked']
    tncCheckedList = isContractor && bidStatus !== vendorBidStatus.SUBMITTED
      ? storeTncCheckedList
      : Array(storeTncCheckedList.length).fill({ checked: true })
  }
  const files = await getTermsConditions({
    isContractor,
    tenderId: general.id,
    status: general.status,
    isTender,
    isAddendum: general.version > 0,
  }) || []
  if (files.length > 0) {
    tnc.showAttachment = true
  }
  const isOldTerm = store.getters['tenders/isOldTerm']
  const tncI18nTermsPath = store.getters['tenders/tncI18nTermsPath']
  tnc.files = files
  const validityPeriodPart = `<span style="font-style: Italic;">${validityPeriod} ${i18n.t('days')}</span>`
  const currencyCode = lodash.get(general, 'purchaseRequisition.currencyCode')
  const totalAmountPart = `${currencyCode}<span style="color: #7E9943;">${totalAmount}</span>`
  const terms = tncCheckedList.map((item, index) => {
    const termI18nItem = i18n.te(tncI18nTermsPath)
      ? {
        content: i18n.t(
          `${tncI18nTermsPath}[${index}].value`,
          { validityPeriod: validityPeriodPart, totalFee: totalAmountPart },
        ),
        itqExclude: i18n.t(`${tncI18nTermsPath}`)[index].itqExclude,
      }
      : {}
    return {
      checked: item.checked,
      text: termI18nItem.content,
      itqExclude: termI18nItem.itqExclude,
    }
  })
  if (isOldTerm) {
    terms[terms.length - 1].text += ` ${currencyCode}<span style="color: #7E9943;">${totalAmount}</span>`
  }
  tnc.terms = terms.filter(item => {
    return isTender || !item.itqExclude
  })
  return tnc
}

export async function buildBlankPdfRequest (tender) {
  const general = lodash.cloneDeep(lodash.get(tender, 'general'))
  updateGeneral((general))

  const isTender = general.tenderType === 'TENDER'
  const validityPeriod = lodash.get(general, 'validityPeriod')
  const storeTncCheckedList = store.getters['tenders/tncChecked']
  const terms = await buildTerms({
    general,
    isTender,
    validityPeriod,
    totalAmount: ` ${i18n.t('emptyValue')}`,
    tncCheckedList: Array(storeTncCheckedList.length).fill({ checked: false }),
  })

  const omittedGeneral = lodash.omit(general, ['bids', 'histories', 'purchaseRequisition', 'service', 'analysis', 'creator', 'purchaseOrders', 'contracts'])

  const engineerFiles = await getAttachments(general.id)
  const picContacts = getTenderPicContacts(general)

  const serviceItems = lodash.cloneDeep(lodash.get(tender, ['service']) || [])
  const filteredItems = filterAddendumRemovedItems(serviceItems)

  const request = {
    general: Object.assign({}, omittedGeneral, picContacts, {
      issueHistories: getIssueDateArr(tender),
    }),
    currencyCode: lodash.get(general, 'purchaseRequisition.currencyCode'),
    itemServices: { items: filteredItems },
    showAttachment: true,
    showEngineerAttachment: true,
    engineerFiles: engineerFiles,
    sow: { ...general.scopeOfWorks },
    isTender,
    tnc: terms,
  }
  return request
}
